// ============================================
// PEPTIDE LIBRARY (reference data)
//
// Server-side paywall (2026-07-10): the client never reads the
// peptides / peptide_stacks base tables. Free content comes from the
// sanitized peptides_free / stacks_free views; premium content
// (starterProtocol, storage_simple, commonProtocols) comes from the
// peptides_premium / stacks_premium views, which return ZERO rows unless
// the caller's JWT belongs to a Plus subscriber. The DB decides
// entitlement - no subscription check lives in this file.
//
// safetySnapshot (side effects / who-should-avoid / disclaimer) is FREE
// as of 2026-07-15 (20260715_safety_snapshot_free.sql): it lives in the
// *_free views and reaches every user, so the Safety detail tab renders
// for free and logged-out visitors. It is intentionally NOT premium.
//
// Research content (trustedVoices, goDeeper, and knowledgeBase) is FREE
// as of 2026-07-23 (20260723_research_content_free.sql). It lives in the
// *_free views and is intentionally NOT merged from premium payloads.
//
// The premium field boundary is defined in
// supabase/migrations/20260710_premium_content_views.sql - keep the
// projections here in sync with it.
// ============================================

import { supabase } from '../../lib/supabase';
import type { BeginnerCard, KnowledgeBaseChunk, Peptide, PeptideStack } from '../types';
import { throwWithContext } from './_shared';

// Slim record shape returned by fetchPeptidesPublicIndex.
// Public-readable mirror of public.peptides containing only id/name/slug,
// kept in sync via DB trigger. Use ONLY for pre-auth flows that need a
// names list (e.g. BetaOnboarding's reconstitution dropdown). Anything
// else should use fetchPeptidesIndex against the free/premium views.
export interface PeptidePublic {
  id: string;
  name: string;
  slug: string | null;
}

// Premium-view row payloads are Partial records: only the gated keys,
// with null where the source record doesn't carry that key.
type PremiumPayload<T> = Partial<T> & { beginnerCard?: Partial<BeginnerCard> };

interface FreeRow {
  id: string;
  data: unknown;
}

/**
 * Merge a premium-view payload into a free-view record, keeping the
 * Peptide/PeptideStack shape stable: premium keys are present only when
 * the premium view returned them (i.e. the user is entitled), and null
 * placeholders from the view's jsonb_build_object are dropped so free
 * records don't grow phantom keys.
 */
export function mergeReference<T extends { beginnerCard?: BeginnerCard }>(
  free: T,
  premium: PremiumPayload<T> | null | undefined,
): T {
  if (!premium) return free;
  const { beginnerCard: premiumCard, ...premiumTop } = premium;
  const merged = { ...free, ...stripNulls(premiumTop) };
  const cardExtras = premiumCard ? stripNulls(premiumCard) : {};
  // Only merge card extras into an EXISTING free card: fabricating a
  // beginnerCard from premium keys alone would produce an object missing
  // the required free fields (oneLiner, usedFor, ...) typed as complete.
  if (free.beginnerCard && Object.keys(cardExtras).length > 0) {
    merged.beginnerCard = { ...free.beginnerCard, ...cardExtras } as BeginnerCard;
  }
  return merged;
}

function stripNulls<T extends object>(obj: T): Partial<T> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value !== null && value !== undefined) out[key] = value;
  }
  return out as Partial<T>;
}

/** Local session check (no network). Anon visitors have no grant on the
 *  premium views, so querying them would 403 - skip instead. */
async function hasSession(): Promise<boolean> {
  const { data } = await supabase.auth.getSession();
  return Boolean(data.session);
}

/** Turn a premium-view row into a merge payload. Handles both select
 *  shapes: `data` (full jsonb payload) and projected columns (aliased
 *  data->key selects). */
function toPremiumPayload<T>(row: Record<string, unknown>): PremiumPayload<T> {
  const { id: _id, data, ...projected } = row;
  const payload = ((data ?? projected) as Record<string, unknown>) ?? {};
  return payload as PremiumPayload<T>;
}

/**
 * Fetch a premium view into an id -> payload map.
 *
 * For non-subscribers the view simply returns zero rows - no error. A
 * real error, though, THROWS: silently degrading a Plus subscriber to
 * free content would get cached for the whole session by useCloudQuery
 * ("paying user sees paywalled tabs" with nothing in the console), so a
 * failed premium read fails the fetch and surfaces the normal retry UI.
 */
async function fetchPremiumMap<T>(
  view: 'peptides_premium' | 'stacks_premium',
  select: string,
): Promise<Map<string, PremiumPayload<T>>> {
  const map = new Map<string, PremiumPayload<T>>();
  if (!(await hasSession())) return map;
  // One retry: a transient blip on this leg would otherwise fail the
  // whole library fetch even for free users (whose premium payload is
  // guaranteed empty anyway).
  let res = await supabase.from(view).select(select);
  if (res.error) res = await supabase.from(view).select(select);
  if (res.error) throwWithContext("Couldn't load part of the peptide library", res.error);
  for (const row of (res.data ?? []) as unknown as Array<Record<string, unknown>>) {
    map.set(row.id as string, toPremiumPayload<T>(row));
  }
  return map;
}

/** Single-record variant of fetchPremiumMap for the by-slug fetchers, so
 *  a detail view doesn't pull every record's knowledge base. */
async function fetchPremiumOne<T>(
  view: 'peptides_premium' | 'stacks_premium',
  id: string,
  select: string,
): Promise<PremiumPayload<T> | null> {
  if (!(await hasSession())) return null;
  let res = await supabase.from(view).select(select).eq('id', id).maybeSingle();
  if (res.error) res = await supabase.from(view).select(select).eq('id', id).maybeSingle();
  if (res.error) throwWithContext("Couldn't load part of the peptide library", res.error);
  if (!res.data) return null;
  return toPremiumPayload<T>(res.data as unknown as Record<string, unknown>);
}

// Light premium projection for list screens: protocols-only gated fields.
const PEPTIDE_PREMIUM_INDEX_SELECT =
  'id,commonProtocols:data->commonProtocols,beginnerCard:data->beginnerCard';

/**
 * Full list fetch. No list consumer renders knowledgeBase (detail pages
 * use fetchPeptideBySlug), so this shares the index implementation and
 * its light premium projection.
 */
export async function fetchPeptides(): Promise<Peptide[]> {
  return fetchPeptidesIndex();
}

/**
 * List-view fetch: every peptide from the free view, enhanced only with
 * the still-gated protocol fields (commonProtocols + beginnerCard
 * starterProtocol/storage_simple). The two queries have no data
 * dependency, so they run concurrently.
 */
export async function fetchPeptidesIndex(): Promise<Peptide[]> {
  // Peptides are authenticated-only content (anon pre-auth flows use
  // fetchPeptidesPublicIndex). Before the view switch, anon reads hit
  // RLS and got an empty list; keep that behavior instead of a 403
  // throw from the ungranted view.
  if (!(await hasSession())) return [];
  const [freeRes, premium] = await Promise.all([
    supabase.from('peptides_free').select('id,data').order('id'),
    fetchPremiumMap<Peptide>('peptides_premium', PEPTIDE_PREMIUM_INDEX_SELECT),
  ]);
  if (freeRes.error) throwWithContext("Couldn't load the peptide library", freeRes.error);
  return ((freeRes.data || []) as FreeRow[])
    .filter((row) => row.data)
    .map((row) => mergeReference(row.data as Peptide, premium.get(row.id)));
}

export async function fetchPeptideBySlug(slug: string): Promise<Peptide | null> {
  const { data, error } = await supabase
    .from('peptides_free')
    .select('id,data')
    .eq('slug', slug)
    .limit(1)
    .single();
  if (error || !data?.data) return null;
  const premium = await fetchPremiumOne<Peptide>('peptides_premium', data.id, 'data');
  return mergeReference(data.data as Peptide, premium);
}

/**
 * Batch the free embedded knowledge bases for stack detail pages: one
 * query for all component peptides instead of a per-component
 * fetchPeptideBySlug fan-out. Supplemental content: failures degrade to
 * an empty list rather than failing the page.
 */
export async function fetchPeptidesKnowledgeBase(
  peptideIds: string[],
): Promise<KnowledgeBaseChunk[]> {
  if (peptideIds.length === 0) return [];
  const { data, error } = await supabase
    .from('peptides_free')
    .select('id,data')
    .in('id', peptideIds);
  if (error || !data) {
    if (error) console.warn('[peptides] knowledge-base batch fetch failed:', error.message);
    return [];
  }
  const chunks: KnowledgeBaseChunk[] = [];
  for (const row of data as unknown as Array<{ id: string; data: { knowledgeBase?: KnowledgeBaseChunk[] } | null }>) {
    if (Array.isArray(row.data?.knowledgeBase)) chunks.push(...row.data.knowledgeBase);
  }
  return chunks;
}

/**
 * Slim, anon-readable peptide name index. Backed by public.peptides_public
 * (synced from public.peptides via DB trigger). Use this in pre-auth flows
 * — e.g. the BetaOnboarding reconstitution dropdown which renders before
 * a Supabase session exists. All other peptide reads should use
 * fetchPeptidesIndex / fetchPeptideBySlug against the free/premium views.
 */
export async function fetchPeptidesPublicIndex(): Promise<PeptidePublic[]> {
  const { data, error } = await supabase
    .from('peptides_public')
    .select('id,name,slug')
    .order('name');
  if (error) throwWithContext("Couldn't load the peptide list", error);
  return (data || []) as PeptidePublic[];
}

export async function fetchStacks(): Promise<PeptideStack[]> {
  const [freeRes, premium] = await Promise.all([
    supabase.from('stacks_free').select('id,data').order('id'),
    fetchPremiumMap<PeptideStack>('stacks_premium', 'id,data'),
  ]);
  if (freeRes.error) throwWithContext("Couldn't load peptide stacks", freeRes.error);
  return ((freeRes.data || []) as FreeRow[])
    .filter((row) => row.data)
    .map((row) => mergeReference(row.data as PeptideStack, premium.get(row.id)));
}

export async function fetchStackBySlug(slug: string): Promise<PeptideStack | null> {
  const { data, error } = await supabase
    .from('stacks_free')
    .select('id,data')
    .eq('slug', slug)
    .limit(1)
    .single();
  if (error || !data?.data) return null;
  const premium = await fetchPremiumOne<PeptideStack>('stacks_premium', data.id, 'data');
  return mergeReference(data.data as PeptideStack, premium);
}
